import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

const DEPARTMENTS = [
  'All', 'Pre-flight', 'CMD',
  'Prepress 1', 'Prepress 2', 'Prepress 3',
  'QC',
  'Corrugation', 'Production', 'Dispatch',
];

const MARQUEE_MODES = [
  { key: 'scroll-up', label: '⬆ Scroll Up',  desc: 'Continuous vertical scroll' },
  { key: 'ticker',    label: '⬅ Ticker',      desc: 'Horizontal news-ticker strip' },
  { key: 'fade',      label: '✦ Fade',         desc: 'Fade in/out one at a time' },
  { key: 'slide',     label: '↑ Slide',        desc: 'Slide cards up one at a time' },
];

// ── Shared card helpers ───────────────────────────────────────────────────────

function msgCardClass(priority) {
  return priority === 'urgent'
    ? 'bg-red-950 border-l-8 border-red-400'
    : 'bg-gray-800 border-l-8 border-blue-500';
}

function UrgentBadge({ fontSize }) {
  return (
    <span
      className="inline-block bg-red-500 text-white font-bold px-2.5 py-0.5 rounded mr-3 align-middle"
      style={{ fontSize: `${Math.max(56, Math.round(fontSize * 0.4))}px` }}
    >
      ⚠ URGENT
    </span>
  );
}

// A message can target several departments; older ones carry only the
// singular field. Returns [] when it goes to everyone (nothing worth showing).
function messageDepartments(msg) {
  const ds = Array.isArray(msg.departments) && msg.departments.length
    ? msg.departments
    : [msg.department].filter(Boolean);
  return ds.includes('All') ? [] : ds;
}

function MetaLine({ msg, fontSize }) {
  const depts = messageDepartments(msg);
  const hasMeta = (msg.location && msg.location !== 'All') ||
                  depts.length > 0 ||
                  msg.created_by;
  if (!hasMeta) return null;
  return (
    <div
      className="mt-2 text-gray-400 flex flex-wrap gap-x-4"
      style={{ fontSize: `${Math.max(56, Math.round(fontSize * 0.42))}px` }}
    >
      {msg.location && msg.location !== 'All' && <span>📍 {msg.location}</span>}
      {depts.length > 0 && <span>🏢 {depts.join(', ')}</span>}
      {msg.created_by && <span>— {msg.created_by}</span>}
    </div>
  );
}

// ── MODE 1: Scroll Up (original) ─────────────────────────────────────────────

// ── MODE 1: Scroll Up ────────────────────────────────────────────────────────

function ScrollUpMarquee({ messages, fontSize }) {
  const doubled = [...messages, ...messages];
  const durationSec = Math.max(18, messages.length * 7);

  return (
    <div className="h-full overflow-hidden py-3">
      <style>{`
        @keyframes tvScrollUp {
          0%   { transform: translateY(0); }
          100% { transform: translateY(-50%); }
        }
        .tv-scroll-track {
          animation: tvScrollUp ${durationSec}s linear infinite;
        }
        .tv-scroll-track:hover { animation-play-state: paused; }
      `}</style>
      <div className="tv-scroll-track">
        {doubled.map((msg, i) => (
          <div
            key={i}
            className={`mx-5 my-3 px-7 py-5 rounded-2xl ${msgCardClass(msg.priority)}`}
            style={{ fontSize: `${fontSize}px`, lineHeight: 1.55 }}
          >
            {msg.priority === 'urgent' && <UrgentBadge fontSize={fontSize} />}
            <span className={msg.priority === 'urgent' ? 'text-red-100' : 'text-white'}>
              {msg.message}
            </span>
            <MetaLine msg={msg} fontSize={fontSize} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── MODE 2: Horizontal Ticker (multi-row, all left) ──────────────────────────

// tickerSpeed: 1=Slow, 2=Normal, 3=Fast, 4=Very Fast
const TICKER_SPEED_MULTIPLIER = { 1: 8.0, 2: 6.0, 3: 4, 4: 2 };

function TickerMarquee({ messages, fontSize, tickerSpeed = 2 }) {
  // Build rows: each message gets its own row.
  // If fewer than 3 messages, repeat them to fill at least 3 rows for visual effect.
  const minRows = 3;
  const rows = [];
  for (let i = 0; i < Math.max(minRows, messages.length); i++) {
    rows.push(messages[i % messages.length]);
  }

  const SEP = '\u00a0\u00a0\u2605\u00a0\u00a0'; // '  ★  '
  const mult = TICKER_SPEED_MULTIPLIER[tickerSpeed] ?? 1.0;

  const tickerFontSize = fontSize; // caller passes the ticker-specific font size
  const rowHeight = tickerFontSize * 2.2;

  return (
    <div className="h-full flex flex-col justify-center overflow-hidden">
      <style>{`
        @keyframes tickerLeft {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .ticker-row-left {
          animation: tickerLeft var(--dur) linear infinite;
          white-space: nowrap;
          display: inline-block;
          will-change: transform;
        }
        .ticker-row-left:hover {
          animation-play-state: paused;
        }
        .ticker-row-wrap {
          overflow: hidden;
          border-bottom: 1px solid rgba(255,255,255,0.07);
          display: flex;
          align-items: center;
        }
      `}</style>

      {rows.map((msg, rowIdx) => {
        const isUrgent = msg.priority === 'urgent';
        const charCount = msg.message.length;
        // Base duration scaled by speed multiplier; rows have slight stagger
        const baseDur = Math.max(6, (charCount * 0.18 + rowIdx * 1.2) * mult);
        const accentColor = isUrgent ? '#f87171' : '#67e8f9';

        const chunk = (
          <span>
            <span style={{ color: accentColor, fontWeight: 900 }}>
              {isUrgent ? '⚠ ' : ''}
              {msg.message}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.35)', fontWeight: 400 }}>{SEP}</span>
          </span>
        );

        return (
          <div
            key={`${rowIdx}-${tickerSpeed}`}
            className="ticker-row-wrap"
            style={{ height: `${rowHeight}px` }}
          >
            <div
              className="ticker-row-left"
              style={{ '--dur': `${baseDur}s`, fontSize: `${tickerFontSize}px`, letterSpacing: '-0.01em' }}
            >
              {Array.from({ length: 8 }, (_, i) => <span key={i}>{chunk}</span>)}
              {Array.from({ length: 8 }, (_, i) => <span key={`m${i}`}>{chunk}</span>)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── MODE 3: Fade (one message at a time) ──────────────────────────────────────

function FadeMarquee({ messages, fontSize }) {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (messages.length <= 1) return;
    const SHOW_MS = 5000;
    const FADE_MS = 800;
    const showTimer = setTimeout(() => setVisible(false), SHOW_MS);
    const nextTimer = setTimeout(() => {
      setIdx(i => (i + 1) % messages.length);
      setVisible(true);
    }, SHOW_MS + FADE_MS);
    return () => { clearTimeout(showTimer); clearTimeout(nextTimer); };
  }, [idx, messages.length]);

  const msg = messages[idx] || messages[0];
  if (!msg) return null;

  return (
    <div className="h-full flex items-center justify-center px-8">
      <div
        className={`w-full max-w-5xl px-10 py-10 rounded-3xl ${msgCardClass(msg.priority)}`}
        style={{
          fontSize: `${fontSize}px`, lineHeight: 1.55,
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.8s ease-in-out',
        }}
      >
        {msg.priority === 'urgent' && (
          <div
            className="inline-block bg-red-500 text-white font-bold px-3 py-1 rounded mb-4"
            style={{ fontSize: `${Math.max(14, Math.round(fontSize * 0.45))}px` }}
          >
            ⚠ URGENT
          </div>
        )}
        <p className={msg.priority === 'urgent' ? 'text-red-100' : 'text-white'}>
          {msg.message}
        </p>
        <MetaLine msg={msg} fontSize={fontSize} />
        {messages.length > 1 && (
          <div className="flex gap-2 mt-5">
            {messages.map((_, i) => (
              <div key={i} className={`h-1.5 rounded-full transition-all duration-500 ${
                i === idx ? 'bg-blue-400 w-6' : 'bg-gray-600 w-1.5'
              }`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── MODE 4: Slide Up (card pushes up one at a time) ───────────────────────────

function SlideMarquee({ messages, fontSize }) {
  const [idx, setIdx] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (messages.length <= 1) return;
    const SHOW_MS = 4500;
    const ANIM_MS = 600;
    const timer = setTimeout(() => {
      setLeaving(true);
      setTimeout(() => {
        setIdx(i => (i + 1) % messages.length);
        setAnimKey(k => k + 1);
        setLeaving(false);
      }, ANIM_MS);
    }, SHOW_MS);
    return () => clearTimeout(timer);
  }, [idx, messages.length]);

  const msg = messages[idx] || messages[0];
  if (!msg) return null;

  return (
    <div className="h-full flex items-center justify-center px-8 overflow-hidden">
      <style>{`
        @keyframes slideInUp {
          from { transform: translateY(60px); opacity: 0; }
          to   { transform: translateY(0);   opacity: 1; }
        }
        @keyframes slideOutUp {
          from { transform: translateY(0);    opacity: 1; }
          to   { transform: translateY(-60px); opacity: 0; }
        }
      `}</style>
      <div
        key={animKey}
        className={`w-full max-w-5xl px-10 py-10 rounded-3xl ${msgCardClass(msg.priority)}`}
        style={{
          fontSize: `${fontSize}px`, lineHeight: 1.55,
          animation: leaving
            ? 'slideOutUp 0.6s cubic-bezier(0.55,0,1,0.45) forwards'
            : 'slideInUp 0.6s cubic-bezier(0.22,1,0.36,1) forwards',
        }}
      >
        {msg.priority === 'urgent' && (
          <div
            className="inline-block bg-red-500 text-white font-bold px-3 py-1 rounded mb-4"
            style={{ fontSize: `${Math.max(14, Math.round(fontSize * 0.45))}px` }}
          >
            ⚠ URGENT
          </div>
        )}
        <p className={msg.priority === 'urgent' ? 'text-red-100' : 'text-white'}>
          {msg.message}
        </p>
        <MetaLine msg={msg} fontSize={fontSize} />
        {messages.length > 1 && (
          <div className="mt-5 flex gap-2">
            {messages.map((_, i) => (
              <div key={i} className={`h-1 rounded-full flex-1 ${
                i === idx ? 'bg-blue-400' : 'bg-gray-700'
              }`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Single-message static display ─────────────────────────────────────────────

function SingleMessage({ message, fontSize }) {
  return (
    <div className="flex items-center justify-center h-full px-8">
      <div
        className={`w-full max-w-5xl px-10 py-10 rounded-3xl ${msgCardClass(message.priority)}`}
        style={{ fontSize: `${fontSize}px`, lineHeight: 1.55 }}
      >
        {message.priority === 'urgent' && (
          <div
            className="inline-block bg-red-500 text-white font-bold px-3 py-1 rounded mb-4"
            style={{ fontSize: `${Math.max(14, Math.round(fontSize * 0.45))}px` }}
          >
            ⚠ URGENT
          </div>
        )}
        <p className={message.priority === 'urgent' ? 'text-red-100' : 'text-white'}>
          {message.message}
        </p>
        <MetaLine msg={message} fontSize={fontSize} />
      </div>
    </div>
  );
}

// ── Location / Department selector screen ─────────────────────────────────────

function SelectorScreen({ onStart }) {
  const [location, setLocation] = useState('');
  const [department, setDepartment] = useState('');
  const [marqueeMode, setMarqueeMode] = useState('scroll-up');
  const [locations, setLocations] = useState([]);
  const [loadingLoc, setLoadingLoc] = useState(true);

  useEffect(() => {
    fetch('/api/messages/locations')
      .then(r => r.json())
      .then(data => setLocations(Array.isArray(data) ? data : []))
      .catch(() => setLocations([]))
      .finally(() => setLoadingLoc(false));
  }, []);

  return (
    <div className="bg-gray-950 flex items-center justify-center p-8" style={{ height: '100svh', overflow: 'hidden' }}>
      <div className="bg-gray-800 rounded-3xl p-10 w-full max-w-md shadow-2xl border border-gray-700">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">📺</span>
          </div>
          <h1 className="text-3xl font-bold text-white">TV Display</h1>
          <p className="text-gray-400 mt-2 text-sm">
            Select location and department to start
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Location</label>
            <select
              value={location}
              onChange={e => setLocation(e.target.value)}
              className="w-full px-4 py-3 bg-gray-700 text-white rounded-xl border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
              disabled={loadingLoc}
            >
              <option value="">
                {loadingLoc ? 'Loading…' : 'Select Location…'}
              </option>
              {locations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Department</label>
            <select
              value={department}
              onChange={e => setDepartment(e.target.value)}
              className="w-full px-4 py-3 bg-gray-700 text-white rounded-xl border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
            >
              <option value="">Select Department…</option>
              {DEPARTMENTS.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          {/* Display style picker */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Display Style</label>
            <div className="grid grid-cols-2 gap-2">
              {MARQUEE_MODES.map(m => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setMarqueeMode(m.key)}
                  className={`px-3 py-2.5 rounded-xl border text-left transition-all ${
                    marqueeMode === m.key
                      ? 'bg-blue-600 border-blue-500 text-white'
                      : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="font-medium text-sm">{m.label}</div>
                  <div className="text-xs mt-0.5 opacity-70">{m.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Confirm the speakers work before the screen is left unattended. */}
          <button
            onClick={() => bell.ring()}
            className="w-full py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors mt-2"
          >
            🔔 Test bell sound
          </button>

          <button
            onClick={() => {
              // Must happen inside the click: this is the page's only user
              // gesture, and without it the browser blocks all audio.
              bell.unlock();
              onStart(location, department, marqueeMode);
            }}
            disabled={!location || !department}
            className="w-full py-4 bg-blue-600 text-white rounded-xl text-lg font-semibold hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors mt-2"
          >
            Launch Display →
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Bell ────────────────────────────────────────────────────────────────────
/**
 * A chime for new messages, synthesised rather than loaded from a file so
 * there is no asset to deploy and nothing to 404 on a screen nobody is
 * watching.
 *
 * Browsers refuse to play audio until the user has interacted with the page,
 * which is why `unlock()` is called from the Launch Display click. That click
 * is the only gesture a wall-mounted TV ever gets, so if the page reloads on
 * its own the bell stays silent until someone launches it again.
 */
const bell = {
  ctx: null,

  unlock() {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!this.ctx) this.ctx = new Ctx();
      if (this.ctx.state === 'suspended') this.ctx.resume();
    } catch {
      this.ctx = null;
    }
  },

  ring() {
    // Called straight from a click during testing, so unlock here too rather
    // than failing silently when the gesture has not happened yet.
    if (!this.ctx) this.unlock();
    const ctx = this.ctx;
    if (!ctx) {
      console.warn('[TV bell] No AudioContext — browser blocked audio.');
      return;
    }
    try {
      if (ctx.state === 'suspended') ctx.resume();
      // Two quick strikes a fifth apart — carries across a noisy plant floor
      // better than a single tone.
      [[880, 0], [1320, 0.18]].forEach(([freq, delay]) => {
        const t0 = ctx.currentTime + delay;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, t0);
        // Fast attack, long decay: a struck bell rather than a beep.
        gain.gain.setValueAtTime(0.0001, t0);
        gain.gain.exponentialRampToValueAtTime(0.35, t0 + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.1);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t0);
        osc.stop(t0 + 1.2);
      });
      console.log('[TV bell] ring, ctx state:', ctx.state);
    } catch (e) {
      /* never let a missing speaker break the display */
      console.warn('[TV bell] failed:', e);
    }
  },
};

// Debug hook so the bell can be rung from the console on a screen that is
// already running: __tvBell.ring()
if (typeof window !== 'undefined') window.__tvBell = bell;

// ── Clock component ───────────────────────────────────────────────────────────

function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="text-right">
      <div className="text-white font-mono font-bold text-2xl">
        {now.toLocaleTimeString('en-GB')}
      </div>
      <div className="text-blue-200 text-xs mt-0.5">
        {now.toLocaleDateString('en-GB', {
          weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
        })}
      </div>
    </div>
  );
}

// ── Main TV Display ───────────────────────────────────────────────────────────

export default function TVDisplay() {
  const [step, setStep] = useState('select');

  // Lock html / body / #root so the TV display fills exactly the screen
  useEffect(() => {
    const targets = [
      document.documentElement,
      document.body,
      document.getElementById('root'),
    ].filter(Boolean);
    const prev = targets.map(el => ({ height: el.style.height, overflow: el.style.overflow, margin: el.style.margin, padding: el.style.padding }));
    targets.forEach(el => {
      el.style.height = '100%';
      el.style.overflow = 'hidden';
      el.style.margin = '0';
      el.style.padding = '0';
    });
    return () => {
      targets.forEach((el, i) => {
        el.style.height = prev[i].height;
        el.style.overflow = prev[i].overflow;
        el.style.margin = prev[i].margin;
        el.style.padding = prev[i].padding;
      });
    };
  }, []);
  const [location, setLocation] = useState('');
  const [department, setDepartment] = useState('');
  const [marqueeMode, setMarqueeMode] = useState('scroll-up');
  const [tickerSpeed, setTickerSpeed] = useState(2); // 1=Slow 2=Normal 3=Fast 4=Very Fast
  const [tickerFontSize, setTickerFontSize] = useState(50); // 50–90, ticker-only
  const [messages, setMessages] = useState([]);
  const [fontSize, setFontSize] = useState(32);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [msgError, setMsgError] = useState('');
  const [newMsgFlash, setNewMsgFlash] = useState(false);
  const [tickerPageIdx, setTickerPageIdx] = useState(0); // page of 6 for ticker rotation
  const intervalRef = useRef(null);
  const tickerPageRef = useRef(null);
  const prevIdsRef = useRef(null); // track message IDs to detect additions

  // Bell on/off. Remembered per screen, so a display in a quiet area stays
  // muted across reloads. Read through a ref because fetchMessages is memoised
  // and would otherwise capture the value from first render.
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem('tv_bell') !== 'off'; } catch { return true; }
  });
  const soundOnRef = useRef(soundOn);
  useEffect(() => {
    soundOnRef.current = soundOn;
    try { localStorage.setItem('tv_bell', soundOn ? 'on' : 'off'); } catch { /* private mode */ }
  }, [soundOn]);

  const fetchMessages = useCallback(async (loc, dept, isBackground = false) => {
    if (!isBackground) setLoadingMsg(true);
    setMsgError('');
    try {
      const params = new URLSearchParams({ active: 'true' });
      if (loc && loc !== 'All') params.set('location', loc);
      if (dept && dept !== 'All') params.set('department', dept);
      const res = await fetch(`/api/messages?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to load');
      const json = await res.json();
      const data = Array.isArray(json) ? json : [];
      // Detect new messages by comparing IDs to previous poll
      if (prevIdsRef.current !== null) {
        const prevSet = new Set(prevIdsRef.current);
        const hasNew = data.some(m => !prevSet.has(m.id));
        if (hasNew) {
          setNewMsgFlash(true);
          setTimeout(() => setNewMsgFlash(false), 3000);
          // Rings once per poll however many messages arrived — a burst of
          // five would otherwise overlap into noise.
          if (soundOnRef.current) bell.ring();
        }
      }
      prevIdsRef.current = data.map(m => m.id);
      setMessages(data);
    } catch {
      setMsgError('Could not load messages. Retrying…');
    } finally {
      setLoadingMsg(false);
    }
  }, []);

  const handleStart = useCallback((loc, dept, mode) => {
    setLocation(loc);
    setDepartment(dept);
    setMarqueeMode(mode);
    setStep('display');
    setTickerPageIdx(0);
    prevIdsRef.current = null;
    fetchMessages(loc, dept, false);
    // Poll every 8 seconds for near-real-time updates
    intervalRef.current = setInterval(() => fetchMessages(loc, dept, true), 8000);
  }, [fetchMessages]);

  // Ticker page rotation: cycles pages of 6 on the configured interval (ticker mode only)
  useEffect(() => {
    if (step !== 'display' || marqueeMode !== 'ticker') {
      if (tickerPageRef.current) { clearInterval(tickerPageRef.current); tickerPageRef.current = null; }
      return;
    }
    const rotationMs = (parseInt(localStorage.getItem('alertMsgRotationSecs') || '120', 10)) * 1000;
    tickerPageRef.current = setInterval(() => setTickerPageIdx(p => p + 1), rotationMs);
    return () => { if (tickerPageRef.current) { clearInterval(tickerPageRef.current); tickerPageRef.current = null; } };
  }, [step, marqueeMode]);

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  };

  const handleBack = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setMessages([]);
    setStep('select');
  };

  if (step === 'select') {
    return <SelectorScreen onStart={handleStart} />;
  }

  const activeMessages = messages.filter(m => m.active !== false);

  // Ticker page logic: split into pages of 6, cycle via tickerPageIdx
  const TICKER_PAGE_SIZE = 6;
  const tickerPages = [];
  for (let i = 0; i < activeMessages.length; i += TICKER_PAGE_SIZE) {
    tickerPages.push(activeMessages.slice(i, i + TICKER_PAGE_SIZE));
  }
  const tickerTotalPages = tickerPages.length;
  const tickerSafePage = tickerTotalPages > 0 ? tickerPageIdx % tickerTotalPages : 0;
  const tickerCurrentPage = tickerTotalPages > 0 ? tickerPages[tickerSafePage] : [];

  const renderMessages = () => {
    if (loadingMsg) return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500 text-2xl animate-pulse">Loading messages…</div>
      </div>
    );
    if (msgError) return (
      <div className="flex items-center justify-center h-full">
        <div className="text-red-400 text-xl">{msgError}</div>
      </div>
    );
    if (activeMessages.length === 0) return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="text-6xl mb-4 opacity-30">📺</div>
          <p className="text-gray-500 text-2xl">No active messages</p>
        </div>
      </div>
    );

    switch (marqueeMode) {
      case 'ticker':   return (
        <div key={`ticker-page-${tickerSafePage}`} className="ticker-page-enter h-full">
          <TickerMarquee messages={tickerCurrentPage.length > 0 ? tickerCurrentPage : activeMessages} fontSize={tickerFontSize} tickerSpeed={tickerSpeed} />
        </div>
      );
      case 'fade':     return <FadeMarquee  messages={activeMessages} fontSize={fontSize} />;
      case 'slide':    return <SlideMarquee messages={activeMessages} fontSize={fontSize} />;
      case 'scroll-up':
      default:
        if (activeMessages.length === 1) return <SingleMessage message={activeMessages[0]} fontSize={fontSize} />;
        return <ScrollUpMarquee messages={activeMessages} fontSize={fontSize} />;
    }
  };

  return (
    <div
      className="bg-gray-950 flex flex-col select-none"
      style={{ height: '100svh', overflow: 'hidden', fontFamily: "'Segoe UI', Arial, sans-serif" }}
    >
      {/* Header */}
      <div className={`px-8 py-4 flex items-center justify-between flex-shrink-0 border-b border-blue-800 transition-colors duration-700 ${newMsgFlash ? 'bg-green-900' : 'bg-blue-900'}`}>
        <div>
          <div className="text-blue-300 text-xs font-semibold uppercase tracking-widest mb-0.5 flex items-center gap-2">
            Important Messages
            {newMsgFlash && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500 text-white text-xs rounded-full animate-pulse">
                ● New message
              </span>
            )}
            {marqueeMode === 'ticker' && tickerTotalPages > 1 && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-blue-800 text-blue-200 text-xs rounded-full font-normal">
                <span className="font-mono font-bold">{tickerSafePage + 1} / {tickerTotalPages}</span>
                {tickerPages.map((_, i) => (
                  <span key={i} className={`inline-block rounded-full transition-all duration-500 ${
                    i === tickerSafePage ? 'w-4 h-2 bg-white' : 'w-2 h-2 bg-blue-500 opacity-50'
                  }`} />
                ))}
              </span>
            )}
          </div>
          <h1 className="text-white font-bold text-xl leading-tight">
            {location !== 'All' ? location : 'All Locations'}
            <span className="text-blue-300 mx-2">·</span>
            {department !== 'All' ? department : 'All Departments'}
          </h1>
        </div>
        <div className="flex items-center gap-4">
          {/* Small and low-contrast: this is for whoever sets the screen up,
              not for the people reading it from across the floor. */}
          <button
            onClick={() => {
              const next = !soundOn;
              setSoundOn(next);
              // Ring when switching on, so the click doubles as a speaker test.
              if (next) bell.ring();
            }}
            title={soundOn ? 'Bell on — click to mute' : 'Bell muted — click to unmute'}
            className="text-blue-300 hover:text-white transition-colors opacity-40 hover:opacity-100"
          >
            {soundOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
          <Clock />
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-hidden">
        <style>{`
          @keyframes tickerPageIn {
            from { transform: translateY(32px); opacity: 0; }
            to   { transform: translateY(0);   opacity: 1; }
          }
          .ticker-page-enter { animation: tickerPageIn 0.6s cubic-bezier(0.22,1,0.36,1) both; }
        `}</style>
        {renderMessages()}
      </div>

      {/* Controls footer */}
      <div className="bg-gray-900 border-t border-gray-800 px-4 py-2.5 flex items-center gap-3 flex-shrink-0">
        {/* Back */}
        <button
          onClick={handleBack}
          className="text-gray-400 hover:text-white text-sm px-3 py-1.5 rounded-lg border border-gray-700 hover:border-gray-500 transition-colors flex-shrink-0"
        >
          ← Change
        </button>

        {/* Mode switcher (live) */}
        <div className="flex items-center gap-1 flex-1 justify-center flex-wrap">
          {MARQUEE_MODES.map(m => (
            <button
              key={m.key}
              onClick={() => setMarqueeMode(m.key)}
              title={m.desc}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                marqueeMode === m.key
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700 border border-gray-700'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* Ticker speed (only visible in ticker mode) */}
        {marqueeMode === 'ticker' && (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-gray-500 text-xs">Speed:</span>
            {[{v:1,label:'1x'},{v:2,label:'2x'},{v:3,label:'3x'},{v:4,label:'4x'}].map(({v, label}) => (
              <button
                key={v}
                onClick={() => setTickerSpeed(v)}
                className={`px-2 py-1 rounded text-xs font-mono transition-all ${
                  tickerSpeed === v
                    ? 'bg-cyan-600 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700 border border-gray-700'
                }`}
              >{label}</button>
            ))}
          </div>
        )}

        {/* Font size */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => marqueeMode === 'ticker'
              ? setTickerFontSize(s => Math.max(50, s - 4))
              : setFontSize(s => Math.max(16, s - 4))
            }
            className="w-7 h-7 bg-gray-700 text-white rounded-lg hover:bg-gray-600 font-bold transition-colors"
          >−</button>
          <span className="text-white text-xs w-7 text-center font-mono">
            {marqueeMode === 'ticker' ? tickerFontSize : fontSize}
          </span>
          <button
            onClick={() => marqueeMode === 'ticker'
              ? setTickerFontSize(s => Math.min(90, s + 4))
              : setFontSize(s => Math.min(80, s + 4))
            }
            className="w-7 h-7 bg-gray-700 text-white rounded-lg hover:bg-gray-600 font-bold transition-colors"
          >+</button>
        </div>

        {/* Fullscreen */}
        <button
          onClick={toggleFullscreen}
          className="text-gray-400 hover:text-white text-sm px-3 py-1.5 rounded-lg border border-gray-700 hover:border-gray-500 transition-colors flex-shrink-0"
        >
          ⛶
        </button>

        {/* Live indicator */}
        <span className="flex items-center gap-1 text-xs text-gray-500 flex-shrink-0" title="Auto-updates every 8 seconds">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block" />
          Live
        </span>
      </div>
    </div>
  );
}
